import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import Nav from "@/components/Nav";
import { useAuth } from "@/context/AuthContext";
import {
  ArrowRight,
  Buildings,
  Clock,
  MagnifyingGlass,
  Phone,
  Plus,
  Trash,
  User,
  X,
} from "@phosphor-icons/react";
import { toast } from "sonner";

function formatDate(iso) {
  const d = new Date(iso);
  return d.toLocaleString([], {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatDur(seconds) {
  if (!seconds) return "—";
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.floor(seconds % 60);
  return `${minutes}:${String(remainingSeconds).padStart(2, "0")}`;
}

function firstName(user) {
  const source = user?.name?.trim();
  return source ? source.split(/\s+/)[0] : null;
}

export default function DashboardPage() {
  const { user } = useAuth();
  const [calls, setCalls] = useState([]);
  const [query, setQuery] = useState(() => sessionStorage.getItem("callsect:dashboard:query") || "");
  const [loading, setLoading] = useState(true);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deletingCallId, setDeletingCallId] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/calls");
        setCalls(data);
      } catch {
        toast.error("Failed to load calls");
      } finally {
        setLoading(false);
      }
    })();
  }, []);


  useEffect(() => {
    sessionStorage.setItem("callsect:dashboard:query", query);
  }, [query]);

  useEffect(() => {
    const saveScroll = () => {
      sessionStorage.setItem("callsect:dashboard:scroll", String(window.scrollY));
    };
    window.addEventListener("scroll", saveScroll, { passive: true });
    return () => {
      saveScroll();
      window.removeEventListener("scroll", saveScroll);
    };
  }, []);

  useEffect(() => {
    if (loading) return undefined;
    const savedScroll = Number(sessionStorage.getItem("callsect:dashboard:scroll") || 0);
    const frame = window.requestAnimationFrame(() => window.scrollTo({ top: savedScroll, left: 0 }));
    return () => window.cancelAnimationFrame(frame);
  }, [loading]);

  const deleteCall = async () => {
    if (!pendingDelete || deletingCallId) return;

    setDeletingCallId(pendingDelete.id);
    try {
      await api.delete(`/calls/${pendingDelete.id}`);
      setCalls((current) => current.filter((call) => call.id !== pendingDelete.id));
      setPendingDelete(null);
      toast.success("Call deleted");
    } catch (error) {
      const message = error?.response?.data?.detail || "The call could not be deleted";
      toast.error(message);
    } finally {
      setDeletingCallId(null);
    }
  };

  const normalizedQuery = query.trim().toLowerCase();
  const filtered = calls.filter((call) =>
    !normalizedQuery ||
    (call.title || "").toLowerCase().includes(normalizedQuery) ||
    (call.customer_name || "").toLowerCase().includes(normalizedQuery) ||
    (call.company || "").toLowerCase().includes(normalizedQuery)
  );

  const stats = {
    total: calls.length,
    thisWeek: calls.filter(
      (call) =>
        new Date(call.occurred_at || call.created_at) >
        new Date(Date.now() - 7 * 864e5)
    ).length,
    customers: new Set(
      calls.map((call) => call.relationship_id || call.customer_name).filter(Boolean)
    ).size,
  };

  const name = firstName(user);

  return (
    <div className="dashboard-page">
      <Nav />

      <main className="dashboard-shell">
        <header className="dashboard-workspace-header">
          <div>
            <div className="dashboard-section-label">Dashboard</div>
            <h1>Dashboard</h1>
            <p>
              {name ? `Welcome back, ${name}. ` : ""}
              Here&apos;s what&apos;s happening with your conversations.
            </p>
          </div>

          <Link to="/upload" data-testid="new-call-btn" className="dashboard-primary-button">
            <Plus size={16} weight="bold" />
            Upload new call
          </Link>
        </header>

        <section className="dashboard-stats" aria-label="Call overview">
          <StatCard
            label="Total calls"
            value={stats.total}
            detail="All conversations"
            icon={Phone}
            tone="violet"
            testId="stat-total"
          />
          <StatCard
            label="This week"
            value={stats.thisWeek}
            detail="Last 7 days"
            icon={Clock}
            tone="blue"
            testId="stat-week"
          />
          <StatCard
            label="Customers"
            value={stats.customers}
            detail="Unique relationships"
            icon={Buildings}
            tone="cyan"
            testId="stat-customers"
          />
        </section>

        <section className="dashboard-library">
          <div className="dashboard-library__toolbar">
            <div>
              <div className="dashboard-section-label">Conversation library</div>
              <h2>Recent conversations</h2>
            </div>

            <label className="dashboard-search">
              <MagnifyingGlass size={17} aria-hidden="true" />
              <input
                data-testid="search-input"
                placeholder="Search calls, customers, companies..."
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                aria-label="Search calls, customers, and companies"
              />
              {query && (
                <span className="dashboard-search__count">
                  {filtered.length}/{calls.length}
                </span>
              )}
            </label>
          </div>

          {loading ? (
            <div className="dashboard-loading" role="status" aria-live="polite">
              {[0, 1, 2, 3].map((item) => (
                <div key={item} className="dashboard-skeleton">
                  <span className="dashboard-skeleton__mark" />
                  <span className="dashboard-skeleton__copy">
                    <i />
                    <i />
                  </span>
                </div>
              ))}
              <span className="sr-only">Loading calls...</span>
            </div>
          ) : filtered.length === 0 ? (
            <div className="dashboard-empty">
              <div className="dashboard-empty__icon">
                {calls.length === 0 ? (
                  <Phone size={26} weight="duotone" />
                ) : (
                  <MagnifyingGlass size={26} weight="duotone" />
                )}
              </div>
              <strong>
                {calls.length === 0
                  ? "Your conversation library is empty."
                  : "No matching conversations."}
              </strong>
              <p>
                {calls.length === 0
                  ? "Upload your first call and Callsect will turn it into findings, notes, and practical next steps."
                  : "Try a different call title, customer, or company."}
              </p>
              {calls.length === 0 && (
                <Link
                  to="/upload"
                  data-testid="empty-upload-btn"
                  className="dashboard-primary-button dashboard-primary-button--compact"
                >
                  <Plus size={15} weight="bold" />
                  Upload a call
                </Link>
              )}
            </div>
          ) : (
            <div className="dashboard-call-list">
              {filtered.map((call, index) => (
                <article
                  key={call.id}
                  className="dashboard-call-card"
                  style={{ animationDelay: `${Math.min(index, 8) * 35}ms` }}
                >
                  <Link
                    to={`/calls/${call.id}`}
                    state={{ from: "dashboard" }}
                    data-testid={`call-card-${call.id}`}
                    className="dashboard-call-card__main"
                    aria-label={`Open ${call.title || "call"}`}
                  >
                    <span className="dashboard-call-card__mark" aria-hidden="true">
                      <Phone size={18} weight="duotone" />
                    </span>

                    <span className="dashboard-call-card__content">
                      <span className="dashboard-call-card__title">
                        {call.title || "Untitled call"}
                      </span>
                      <span className="dashboard-call-card__meta">
                        {call.customer_name && (
                          <span>
                            <User size={13} weight="fill" />
                            {call.customer_name}
                          </span>
                        )}
                        {call.company && (
                          <span>
                            <Buildings size={13} weight="fill" />
                            {call.company}
                          </span>
                        )}
                        <span>
                          <Clock size={13} weight="fill" />
                          {formatDur(call.duration)}
                        </span>
                      </span>
                    </span>

                    <span className="dashboard-call-card__date">
                      {formatDate(call.occurred_at || call.created_at)}
                    </span>
                  </Link>

                  <button
                    type="button"
                    className="dashboard-call-card__delete"
                    onClick={() => setPendingDelete(call)}
                    data-testid={`delete-call-${call.id}`}
                    aria-label={`Delete ${call.title || "call"}`}
                    title={`Delete ${call.title || "call"}`}
                  >
                    <Trash size={16} weight="bold" aria-hidden="true" />
                  </button>

                  <Link
                    to={`/calls/${call.id}`}
                    state={{ from: "dashboard" }}
                    className="dashboard-call-card__open"
                    aria-label={`Open ${call.title || "call"}`}
                  >
                    <ArrowRight size={16} weight="bold" aria-hidden="true" />
                  </Link>
                </article>
              ))}
            </div>
          )}
        </section>

        {pendingDelete && (
          <div
            className="call-detail-dialog-backdrop"
            role="dialog"
            aria-modal="true"
            aria-labelledby="dashboard-delete-call-title"
            data-testid="dashboard-delete-call-dialog"
          >
            <div className="call-detail-dialog dashboard-delete-dialog">
              <div className="call-detail-dialog__header">
                <div>
                  <div className="call-detail-dialog__danger">
                    <Trash size={20} weight="fill" />
                  </div>
                  <h2 id="dashboard-delete-call-title">Delete this call?</h2>
                </div>
                <button
                  type="button"
                  onClick={() => setPendingDelete(null)}
                  disabled={Boolean(deletingCallId)}
                  aria-label="Close"
                  className="call-detail-dialog__close"
                >
                  <X size={18} />
                </button>
              </div>
              <p>
                <strong>{pendingDelete.title || "Untitled call"}</strong>
                {pendingDelete.customer_name ? ` for ${pendingDelete.customer_name}` : ""} will be
                permanently removed, including its transcript, analysis, coaching and CRM notes.
              </p>
              <small>This action cannot be undone.</small>
              <div className="call-detail-dialog__actions">
                <button
                  type="button"
                  onClick={() => setPendingDelete(null)}
                  disabled={Boolean(deletingCallId)}
                  className="call-detail-secondary-button"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={deleteCall}
                  disabled={Boolean(deletingCallId)}
                  data-testid="dashboard-confirm-delete-call"
                  className="call-detail-danger-button"
                >
                  <Trash size={15} weight="fill" />
                  {deletingCallId ? "Deleting..." : "Delete call"}
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

function StatCard({ label, value, detail, icon: Icon, tone, testId }) {
  return (
    <article className={`dashboard-stat-card is-${tone}`} data-testid={testId}>
      <div className="dashboard-stat-card__head">
        <span className="dashboard-stat-card__icon">
          <Icon size={20} weight="duotone" />
        </span>
        <span className="dashboard-stat-card__label">{label}</span>
      </div>
      <strong>{value}</strong>
      <span className="dashboard-stat-card__detail">{detail}</span>
    </article>
  );
}
